import { isNumber } from 'lodash';
import pluralize from 'pluralize';

import dayjs from 'services/dayjs';

export const getTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getTimeZoneAbbreviation = timezone =>
  dayjs()
    .tz(timezone || getTimezone())
    .format('zzzz')
    .split(' ')
    .map(w => w[0])
    .join('');

/**
 * Converts time into milliseconds
 *
 * @param {String} time - hh:mm:ss
 */
export const convertTimeIntoMilliseconds = time => {
  const [hours, minutes, seconds] = time.split(':');
  return (
    Number(hours) * 60 * 60 * 1000 +
    Number(minutes) * 60 * 1000 +
    Number(seconds) * 1000
  );
};

// https://stackoverflow.com/questions/11887934/how-to-check-if-dst-daylight-saving-time-is-in-effect-and-if-so-the-offset/30280636#30280636
export const isDST = date => {
  const jan = new Date(date.get('year'), 0, 1).getTimezoneOffset();
  const jul = new Date(date.get('year'), 6, 1).getTimezoneOffset();
  return Math.max(jan, jul) !== date.utcOffset();
};

export const renderDateOnElapsedTime = created => {
  const now = dayjs();
  const createdDate = dayjs(created);
  const diffSeconds = now.diff(createdDate, 'second');
  const diffMinutes = now.diff(createdDate, 'minute');
  const diffHours = now.diff(createdDate, 'hour');
  const diffDays = now.diff(createdDate, 'day');

  if (diffSeconds === 0) {
    return 'Just now';
  }
  if (diffMinutes < 1) {
    const secondsAgo = now.diff(createdDate, 'second');
    return `${secondsAgo} ${pluralize('second', secondsAgo)} ago`;
  }
  if (diffHours < 1) {
    const minutesAgo = now.diff(createdDate, 'minute');
    return `${minutesAgo} ${pluralize('minute', minutesAgo)} ago`;
  }
  if (diffHours < 3) {
    return `${diffHours} ${pluralize('hour', diffHours)} ago`;
  }
  if (diffDays < 2) {
    return createdDate.format('MMM D [at] HH:mm');
  }
  return createdDate.format('MMM D, YYYY');
};

export const formatSchoolYear = year => {
  if (!year || !isNumber(year)) {
    return null;
  }
  const startYear = year - 1;
  const endYear = String(year).slice(2); // Get last two digits
  return `${startYear}-${endYear}`;
};

export const convertMeridianTo24h = (meridianTime = '') => {
  const [time, modifier] = meridianTime.split(' ');

  let hours = time.split(':')[0];
  const minutes = time.split(':')[1];

  const invalidHour = !Number(hours) && hours !== '00';
  const invalidMinute = !Number(minutes) && minutes !== '00';

  if (invalidHour || invalidMinute) {
    return '00:00';
  }

  if (hours === '12') {
    hours = '00';
  }

  if (modifier.toLowerCase() === 'pm') {
    hours = parseInt(hours, 10) + 12;
  }

  const padded = hours.toString().padStart(2, '0');

  return `${padded}:${minutes}`;
};

export const convert24hToMeridian = (time24h = '') => {
  try {
    const parts = time24h.split(':');
    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);
    const second = parts.length > 2 ? parseInt(parts[2], 10) : 0;

    if (
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      Number.isNaN(second) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59 ||
      second < 0 ||
      second > 59
    ) {
      return null; // Invalid time
    }

    const meridian = hour < 12 ? 'am' : 'pm';
    let hour12 = hour % 12;
    if (hour12 === 0) {
      hour12 = 12;
    }

    return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${meridian}`;
  } catch (error) {
    console.error(error);
    return null; // Invalid input format
  }
};

export const formatTimeWithLeadingZero = time => {
  if (!time) return time;
  // If already in meridian format (e.g., 05:30 pm), just ensure leading zero
  const meridianMatch = time.match(/^(\d{1,2}):(\d{2})\s?(am|pm)$/i);
  if (meridianMatch) {
    const [, hourRaw, minute, meridian] = meridianMatch;
    const hour = hourRaw.padStart(2, '0');
    return `${hour}:${minute} ${meridian.toLowerCase()}`;
  }
  // If in 24h format, convert to meridian with leading zero
  const meridian = convert24hToMeridian(time);
  if (meridian) return meridian;
  return time;
};

export const createDateTimeRange = ({ date, startTime, endTime }) => {
  const start24 = convertMeridianTo24h(startTime);
  const end24 = convertMeridianTo24h(endTime);

  const startDateObj = dayjs(date);
  const startDateTime = startDateObj
    .hour(parseInt(start24.split(':')[0], 10))
    .minute(parseInt(start24.split(':')[1], 10))
    .second(0);

  const endDateTime = startDateObj
    .hour(parseInt(end24.split(':')[0], 10))
    .minute(parseInt(end24.split(':')[1], 10))
    .second(0);

  return {
    startDateTime,
    endDateTime,
  };
};
